import React, {useEffect, useRef, useState, useMemo, useCallback} from 'react';
import Main from "../../modules/main/main";
import usePermissions from "../../services/auth/usePermissions";
import {useNavigate} from "react-router-dom";
import { AgGridReact } from 'ag-grid-react'; // the AG Grid React Component
import ReactLoading from 'react-loading';

import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {apiUsers} from "../../services/api/users"; // Optional theme CSS
import {capitalizeFirst} from "../../services/common";
import DialogModule from "../../modules/dialog/dialog";
import * as Yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {useForm} from "react-hook-form";
import './users.scss';
function Users(props) {
    const navigate = useNavigate();
    const [userCan] = usePermissions();
    const breadcrumbs = [
        {"link": "/", "name": "Users", "active": true}
    ];

    const gridRef = useRef(); // Optional - for accessing Grid's API
    const [open, setOpen] = React.useState(false);
    const [allRoles, setAllRoles] = useState([]);
    const [error, setError] = useState('');
    const [paginationPageSize, setPaginationPageSize] = useState(25);
    const [loading, setLoading] = useState(true); // Track loading state for initial load
    const isFetchingRef = useRef(false);
    const createDataSourceRef = useRef(null);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const [columnDefs] = useState([
        {
            field: 'name'
        },
        {
            field: 'email'
        },
        {
            headerName: 'Type (Role)',
            valueGetter: params => {
                if (!params.data) return '';
                if (!params.data.roles || !Array.isArray(params.data.roles) || params.data.roles.length === 0) {
                    return '';
                }
                return capitalizeFirst(params.data.roles[0]?.name || '');
            },
            sortable: false,
            filter: false,
        },
        {
            headerName: 'Action',
            cellRenderer: params => {
                if(userCan('edit.users')) {
                    return (
                        <span>
                        <button className={'btn btn-primary mb-2'} onClick={() => navigate('/users/'+params.data.id)}>Edit</button>
                    </span>
                    );
                }

            },
            editable: false,
            sortable: false,
            filter: false,
            minWidth: 100,
            maxWidth: 100,
        }
    ]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true,
        suppressMenu: true,
        cellStyle: { display: 'flex', alignItems: 'center' }
        // filter: 'agTextColumnFilter',
    }), []);

    // Create datasource for infinite scroll row model
    const createDataSource = useCallback(() => {
        return {
            getRows: async (params) => {
                try {
                    isFetchingRef.current = true;
                    // Show loading indicator whenever data is being fetched
                    setLoading(true);
                    
                    // Get filter model from params - AG Grid passes it directly in params.filterModel
                    let filterModel = params.filterModel || {};
                    // Fallback: If filterModel is empty in params, get it from the grid API
                    if (gridRef.current && gridRef.current.api && (!filterModel || Object.keys(filterModel).length === 0)) {
                        const apiFilterModel = gridRef.current.api.getFilterModel();
                        if (apiFilterModel && Object.keys(apiFilterModel).length > 0) {
                            filterModel = apiFilterModel;
                        }
                    }
                    
                    // Check if any filters are active
                    const hasFilters = filterModel && Object.keys(filterModel).length > 0 && 
                        (filterModel.name || filterModel.email);
                    
                    // Calculate page number from startRow
                    // For infinite scroll, startRow is the index of the first row to fetch
                    // When filters are applied, AG Grid will request from startRow=0 after cache purge
                    const page = Math.floor(params.startRow / paginationPageSize) + 1;
                    const sortModel = params.sortModel;
                    
                    let sortParams = null;
                    if (sortModel && sortModel.length > 0) {
                        sortParams = {
                            colId: sortModel[0].colId,
                            sort: sortModel[0].sort
                        };
                    }
                    
                    // Build query string
                    const queryParams = new URLSearchParams();
                    queryParams.append('page', page);
                    queryParams.append('per_page', paginationPageSize);
                    
                    if (sortParams) {
                        queryParams.append('sort_by', sortParams.colId || 'created_at');
                        queryParams.append('sort_order', sortParams.sort || 'desc');
                    }
                    
                    // Add filter parameters
                    // AG Grid text filter structure: { columnName: { filterType: 'text', filter: 'value', type: 'contains' } }
                    // Handle different filter model structures
                    if (filterModel.name) {
                        let nameFilter = '';
                        if (typeof filterModel.name === 'string') {
                            nameFilter = filterModel.name;
                        } else if (filterModel.name.filter !== undefined && filterModel.name.filter !== null) {
                            nameFilter = String(filterModel.name.filter);
                        } else if (filterModel.name.values && Array.isArray(filterModel.name.values) && filterModel.name.values.length > 0) {
                            nameFilter = String(filterModel.name.values[0]);
                        }
                        // Trim only leading/trailing spaces, preserve internal spaces
                        nameFilter = nameFilter ? nameFilter.trim() : '';
                        if (nameFilter !== '') {
                            // URLSearchParams will automatically encode spaces
                            queryParams.append('filter_name', nameFilter);
                        }
                    }
                    if (filterModel.email) {
                        let emailFilter = '';
                        if (typeof filterModel.email === 'string') {
                            emailFilter = filterModel.email;
                        } else if (filterModel.email.filter !== undefined && filterModel.email.filter !== null) {
                            emailFilter = String(filterModel.email.filter);
                        } else if (filterModel.email.values && Array.isArray(filterModel.email.values) && filterModel.email.values.length > 0) {
                            emailFilter = String(filterModel.email.values[0]);
                        }
                        // Trim only leading/trailing spaces, preserve internal spaces
                        emailFilter = emailFilter ? emailFilter.trim() : '';
                        if (emailFilter !== '') {
                            queryParams.append('filter_email', emailFilter);
                        }
                    }
                    
                    const queryString = queryParams.toString();
                    const res = await apiUsers.getAll(queryString);
                    
                    let users = [];
                    let totalRows = 0;
                    
                    // Handle response - apiUsers.getAll returns response.data from handleResponse
                    if (res && res.users) {
                        if (res.users.data && Array.isArray(res.users.data)) {
                            // Laravel paginated response
                            users = res.users.data;
                            totalRows = res.users.total || 0;
                        } else if (Array.isArray(res.users)) {
                            // Non-paginated array response
                            users = res.users;
                            totalRows = users.length;
                        }
                    } else if (Array.isArray(res)) {
                        // Direct array response (fallback)
                        users = res;
                        totalRows = res.length;
                    }
                    
                    // Store roles for the form (only on first load)
                    if (res && res.roles && allRoles.length === 0) {
                        setAllRoles(res.roles);
                    }
                    
                    // Ensure we have valid data before calling success
                    if (!Array.isArray(users)) {
                        users = [];
                    }
                    
                    // For infinite scroll: successCallback(rowsThisPage, lastRow)
                    // lastRow is the index of the last available row (0-indexed)
                    // If totalRows is 1102, the last row index is 1101 (0-indexed)
                    // When filters are active, lastRow should be based on filtered total
                    // If totalRows is 0, set lastRow to 0 (no rows available)
                    const lastRow = totalRows > 0 ? totalRows - 1 : 0;
                    
                    // Call success callback with the data
                    // Note: For infinite scroll, ag-grid expects the rows for the current block
                    // The startRow parameter tells us which rows to fetch
                    params.successCallback(users, lastRow);
                    
                    // Hide loading indicator after successful load
                    setLoading(false);
                    
                } catch (err) {
                    setError('Failed to load users');
                    params.failCallback();
                    // Hide loading indicator on error
                    setLoading(false);
                } finally {
                    isFetchingRef.current = false;
                }
            }
        };
    }, [paginationPageSize, allRoles.length]);
    
    // Store datasource creator in ref for use in other functions
    createDataSourceRef.current = createDataSource;


    const validationSchema = Yup.object().shape({
        name: Yup.string().required('Please enter a name.'),
        email: Yup.string().required('Please enter a email.'),
        password: Yup.string().required('Please Enter a password'),
        passwordConfirm: Yup.string().oneOf([Yup.ref('password'), null], 'Password must match.'),
        role: Yup.string().required('Please select a role'),
    });
    const formOptions = {
        resolver: yupResolver(validationSchema),
        mode: 'onChange',
    };
    const {
        register,
        handleSubmit,
        reset,
        formState
    } = useForm(formOptions);
    const { errors, isValid } = formState;

    function onSave(data) {
        setError('');
        apiUsers.post(data).then((res) => {
          if(res.status){
              setError(res.data);
          }
          else {
              handleClose();
              // Refresh the grid datasource after adding a user
              if (gridRef.current && gridRef.current.api && createDataSourceRef.current) {
                  const dataSource = createDataSourceRef.current();
                  gridRef.current.api.setDatasource(dataSource);
              }
              reset();
              navigate(`/users/${res.user.id}`);
          }
        });
        return false;
    }

    useEffect(() => {
        if(!userCan('view.users')){
            navigate('/');
            return;
        }
        // Grid will load data via datasource when ready
    }, [userCan, navigate])


    return (
        <Main title="Users" breadcrumbs={breadcrumbs} loading={false}>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <label htmlFor="pageSizeSelect" className="mr-2">Page Size:</label>
                    <select
                        id="pageSizeSelect"
                        className="form-control form-control-sm d-inline-block"
                        style={{ width: 'auto' }}
                        value={paginationPageSize}
                        onChange={(e) => {
                            const newPageSize = Number(e.target.value);
                            setPaginationPageSize(newPageSize);
                            setLoading(true); // Show loading when changing page size
                            if (gridRef.current && gridRef.current.api) {
                                // Update grid page size
                                gridRef.current.api.paginationSetPageSize(newPageSize);
                                // Create new datasource with updated page size
                                // We need to create it inline to use the new value immediately
                                const dataSource = {
                                    getRows: async (params) => {
                                        try {
                                            isFetchingRef.current = true;
                                            // Show loading whenever data is being fetched
                                            setLoading(true);
                                            
                                            const page = Math.floor(params.startRow / newPageSize) + 1;
                                            const sortModel = params.sortModel;
                                            // Get filter model from params - AG Grid passes it directly in params.filterModel
                                            let filterModel = params.filterModel || {};
                                            // Fallback: If filterModel is empty in params, get it from the grid API
                                            if (gridRef.current && gridRef.current.api && (!filterModel || Object.keys(filterModel).length === 0)) {
                                                const apiFilterModel = gridRef.current.api.getFilterModel();
                                                if (apiFilterModel && Object.keys(apiFilterModel).length > 0) {
                                                    filterModel = apiFilterModel;
                                                }
                                            }
                                            
                                            let sortParams = null;
                                            if (sortModel && sortModel.length > 0) {
                                                sortParams = {
                                                    colId: sortModel[0].colId,
                                                    sort: sortModel[0].sort
                                                };
                                            }
                                            
                                            const queryParams = new URLSearchParams();
                                            queryParams.append('page', page);
                                            queryParams.append('per_page', newPageSize);
                                            
                                            if (sortParams) {
                                                queryParams.append('sort_by', sortParams.colId || 'created_at');
                                                queryParams.append('sort_order', sortParams.sort || 'desc');
                                            }
                                            
                                            // Add filter parameters
                                            // AG Grid text filter structure: { columnName: { filterType: 'text', filter: 'value', type: 'contains' } }
                                            // Handle different filter model structures
                                            if (filterModel.name) {
                                                let nameFilter = '';
                                                if (typeof filterModel.name === 'string') {
                                                    nameFilter = filterModel.name;
                                                } else if (filterModel.name.filter !== undefined && filterModel.name.filter !== null) {
                                                    nameFilter = String(filterModel.name.filter);
                                                } else if (filterModel.name.values && Array.isArray(filterModel.name.values) && filterModel.name.values.length > 0) {
                                                    nameFilter = String(filterModel.name.values[0]);
                                                }
                                                // Trim only leading/trailing spaces, preserve internal spaces
                                                nameFilter = nameFilter ? nameFilter.trim() : '';
                                                if (nameFilter !== '') {
                                                    queryParams.append('filter_name', nameFilter);
                                                }
                                            }
                                            if (filterModel.email) {
                                                let emailFilter = '';
                                                if (typeof filterModel.email === 'string') {
                                                    emailFilter = filterModel.email;
                                                } else if (filterModel.email.filter !== undefined && filterModel.email.filter !== null) {
                                                    emailFilter = String(filterModel.email.filter);
                                                } else if (filterModel.email.values && Array.isArray(filterModel.email.values) && filterModel.email.values.length > 0) {
                                                    emailFilter = String(filterModel.email.values[0]);
                                                }
                                                // Trim only leading/trailing spaces, preserve internal spaces
                                                emailFilter = emailFilter ? emailFilter.trim() : '';
                                                if (emailFilter !== '') {
                                                    queryParams.append('filter_email', emailFilter);
                                                }
                                            }
                                            
                                            const queryString = queryParams.toString();
                                            const res = await apiUsers.getAll(queryString);
                                            
                                            let users = [];
                                            let totalRows = 0;
                                            
                                            if (res && res.users) {
                                                if (res.users.data && Array.isArray(res.users.data)) {
                                                    users = res.users.data;
                                                    totalRows = res.users.total || 0;
                                                } else if (Array.isArray(res.users)) {
                                                    users = res.users;
                                                    totalRows = users.length;
                                                }
                                            } else if (Array.isArray(res)) {
                                                users = res;
                                                totalRows = res.length;
                                            }
                                            
                                            if (res && res.roles && allRoles.length === 0) {
                                                setAllRoles(res.roles);
                                            }
                                            
                                            if (!Array.isArray(users)) {
                                                users = [];
                                            }
                                            
                                            const lastRow = totalRows > 0 ? totalRows - 1 : undefined;
                                            params.successCallback(users, lastRow);
                                            
                                            // Hide loading after successful load
                                            setLoading(false);
                                            
                                        } catch (err) {
                                            setError('Failed to load users');
                                            params.failCallback();
                                            setLoading(false);
                                        } finally {
                                            isFetchingRef.current = false;
                                        }
                                    }
                                };
                                gridRef.current.api.setDatasource(dataSource);
                                // Reset to first page when page size changes
                                gridRef.current.api.paginationGoToFirstPage();
                            }
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
                {userCan('create.users') &&
                    <button className={'btn btn-primary'} onClick={handleOpen}>Add User</button>
                }
            </div>
            <div className="ag-theme-alpine" style={{ height: 800, width: '100%', position: 'relative' }}>
                <div className={loading ? "overlay" : "overlay d-none"}>
                    <ReactLoading type="spin" color="orange" width={'10%'} height={'10%'} />
                </div>
                <AgGridReact
                    style={{ width: '100%', height: '100%' }}
                    ref={gridRef}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowModelType="infinite"
                    pagination={true}
                    paginationPageSize={paginationPageSize}
                    cacheBlockSize={paginationPageSize}
                    maxBlocksInCache={10}
                    getRowId={(params) => params.data.id}
                    onGridReady={(params) => {
                        if (!params.api) {
                            return;
                        }
                        try {
                            params.api.sizeColumnsToFit();
                            const dataSource = createDataSource();
                            if (dataSource && dataSource.getRows) {
                                params.api.setDatasource(dataSource);
                            }
                        } catch (error) {
                            // Error setting datasource
                        }
                    }}
                    onSortChanged={(params) => {
                        if (params.api && !isFetchingRef.current) {
                            const dataSource = createDataSource();
                            params.api.setDatasource(dataSource);
                        }
                    }}
                    onFilterChanged={(params) => {
                        if (params.api && !isFetchingRef.current) {
                            // Clear the cache when filter changes - this is critical for filters to work
                            params.api.purgeInfiniteCache();
                            // Reset to first page when filter changes
                            params.api.paginationGoToFirstPage();
                            // Create new datasource with updated filters
                            const dataSource = createDataSource();
                            params.api.setDatasource(dataSource);
                        }
                    }}
                    onColumnResized={(event) => event.api.sizeColumnsToFit()}
                    getRowHeight={() => 25}
                ></AgGridReact>
            </div>

            <DialogModule
                open={open}
                onClose={handleClose}
                setOpen={setOpen}
                title="Add User"
                size="md"
                fullWidth={true}
                hideButtons={true}
            >
                <form className="form-horizontal" onSubmit={handleSubmit(onSave)}>
                    {error && <div className="alert alert-danger">
                        <h5><i className="icon fas fa-ban"></i> Error!</h5>
                        {error}
                    </div>}
                    <div className="form-group row">
                        <label htmlFor="inputName" className="col-sm-2 col-form-label">Name</label>
                        <div className="col-sm-10">
                            <input
                                type="text"
                                name="name"
                                {...register('name')}
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Name"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputEmail" className="col-sm-2 col-form-label">Email</label>
                        <div className="col-sm-10">
                            <input
                                type="email"
                                name="email"
                                {...register('email')}
                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                                placeholder="Email"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputExperience" className="col-sm-2 col-form-label">Role</label>
                        <div className="col-sm-10">
                            {/*<textarea className="form-control" id="inputExperience" placeholder="Role"></textarea>*/}
                            <select
                                name="role"
                                className={`form-control ${errors.role ? 'is-invalid' : ''}`}
                                {...register('role')}
                            >
                                {
                                    allRoles.map((role) => (
                                        <option key={role['id']} value={role['id']}>{role['name']}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputSkills" className="col-sm-2 col-form-label">Password</label>
                        <div className="col-sm-10">
                            <input
                                type="password"
                                name="password"
                                {...register('password')}
                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                placeholder="Password"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputSkills" className="col-sm-2 col-form-label">Confirm Password</label>
                        <div className="col-sm-10">
                            <input
                                type="password"
                                name="passwordConfirm"
                                {...register('passwordConfirm')}
                                className={`form-control ${errors.passwordConfirm ? 'is-invalid' : ''}`}
                                placeholder="Confirm Password"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <div className="offset-sm-2 col-sm-10">
                            <button type="submit" className="btn btn-primary" disabled={!isValid}>Add</button>
                        </div>
                    </div>
                </form>
            </DialogModule>
        </Main>
    );
}

export default Users;